// src/lib/s3-image-loader.ts
export const S3_BUCKET_URL =
  'https://kuma-montessori-images.s3.us-east-1.amazonaws.com'
export function getPublicImageUrl(src: string): string {
  if (!src) return ''

  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src
  }

  if (
    src.startsWith('/images/') ||
    src.startsWith('/uploads/') ||
    src.startsWith('/favicon') ||
    src.startsWith('/og-image')
  ) {
    return src
  }

  const key = src.replace(/^\/+/, '')

  return `${S3_BUCKET_URL}/${key}`
}

/**
 * Loader principal para next/image que devuelve un string
 */
export function s3ImageLoader({
  src,
  width,
  quality = 75,
}: {
  src: string
  width: number
  quality?: number
}): string {
  if (!src) return '/placeholder.svg'

  // 1) Rutas absolutas
  if (src.startsWith('http')) {
    try {
      const u = new URL(src)
      const isS3Host =
        u.hostname.includes('s3.') ||
        u.hostname === new URL(S3_BUCKET_URL).hostname

      // Si es una URL directa de S3, reescribir a nuestro endpoint que firma
      if (isS3Host) {
        const key = u.pathname.startsWith('/')
          ? u.pathname.slice(1)
          : u.pathname
        return `/api/images/${encodeURIComponent(key)}`
      }

      // Cualquier otra URL externa: devolver tal cual
      return src
    } catch {
      return src
    }
  }

  // 2) Archivos locales del proyecto (public/...)
  const normalized = src.startsWith('/') ? src : `/${src}`
  if (
    normalized.startsWith('/images/') ||
    normalized.startsWith('/uploads/') ||
    normalized.startsWith('/favicon')
  ) {
    return normalized
  }

  // 3) Keys de S3
  const baseKey = normalized.startsWith('/') ? normalized.slice(1) : normalized
  return `/api/images/${encodeURIComponent(baseKey)}`
}

/**
 * Helpers opcionales
 */
export function getOptimizedImageUrl(
  src: string,
  options: { width?: number; quality?: number; format?: string } = {}
): string {
  const { width = 800, quality = 75 } = options
  return s3ImageLoader({ src, width, quality })
}

export function generateSrcSet(
  src: string,
  options: { quality?: number; format?: string } = {},
  sizes: number[] = [320, 640, 1024, 1280]
): string {
  return sizes
    .map(size => {
      const url = getOptimizedImageUrl(src, { ...options, width: size })
      return `${url} ${size}w`
    })
    .join(', ')
}

export function generateBlurDataURL(
  src: string,
  options: { width?: number; quality?: number; format?: string } = {}
): string {
  return getOptimizedImageUrl(src, {
    ...options,
    width: options.width ?? 10,
    quality: options.quality ?? 20,
    format: options.format ?? 'webp',
  })
}
